import React, { useEffect } from "react";
import { SessionContext } from "../../common/contexts/SessionContext";
import { useIdle } from "../../common/helperfunctions/useIdle";
import { Id, toast } from "react-toastify";
import { useLogout } from "../NavigationBar";
import { showtoast } from "../../common/overlays/Toasts/showtoast";

/**
 * AutoLogout component automatically logs out the user after a period of inactivity.
 * @author @CorradoSurmanowicz
 * It monitors user activity and, based on the `auto_logout_after_minutes` feature from the session,
 * displays a warning toast before logging the user out. If the user remains inactive after the warning,
 * the component triggers the logout process and notifies the user.
 *
 * @param children - The child components to render within AutoLogout.
 *
 * @remarks
 * - Uses `useIdle` hook to track user inactivity and warning state.
 * - Displays a warning toast when the user is about to be logged out.
 * - Calls the `logout` function and shows an error toast when the user is logged out due to inactivity.
 * - Requires `SessionContext` to provide session information.
 */
export const AutoLogout = ({ children }: { children: React.ReactNode }) => {
  const warningToastId = React.useRef<Id | null>(null);
  const logout = useLogout();

  const { session } = React.useContext(SessionContext);

  const dismiss = React.useCallback(() => {
    if (!warningToastId.current) return;
    toast.dismiss(warningToastId.current);
  }, []);

  const autoLogout = React.useMemo(() => session?.features.auto_logout_after_minutes, [session]);
  const { isIdle, showWarning, deltaT } = useIdle(autoLogout, true, dismiss); // 10 seconds idle time, 30 seconds warning time

  const notifyWarning = React.useCallback(() => {
    if (deltaT && (!warningToastId.current || !toast.isActive(warningToastId.current)))
      warningToastId.current = toast(
        `You will be logged out in ${Math.floor(deltaT / 1000 / 60)} minutes due to inactivity.`,
        {
          position: "top-right",
          autoClose: deltaT,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "dark",
        }
      );
  }, [deltaT]);

  useEffect(() => {
    if (showWarning) {
      notifyWarning();
    } else {
      dismiss();
    }
  }, [showWarning, dismiss, notifyWarning]);

  useEffect(() => {
    if (isIdle) {
      showtoast("error", "You have been logged out due to inactivity.");
      logout();
    }
  }, [isIdle, logout]);

  return <>{children}</>;
};
